import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { outlineOf, pageOfHeading } from "../../lib/records";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

// The preview as real pages: the server returns the PDF a generation would make (base64) and pdf.js draws each page on a canvas, one
// sheet per page with its header, footer and page number, on a grey desk. pdf.js (≈ 400 kB) is loaded only when a preview is shown.
let pdfjsLoading = null;
function loadPdfjs() {
  if (!pdfjsLoading) {
    pdfjsLoading = import("pdfjs-dist/build/pdf").then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.js", import.meta.url).toString();
      return pdfjs;
    });
  }
  return pdfjsLoading;
}

function base64Bytes(b64) {
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

const PAGE_GAP_PX = 12;

export function PagedPreview({ pdf, testid, onError }) {
  const host = useRef(null);
  const holder = useRef(null);
  const doc = useRef(null);
  const runId = useRef(0);
  const [width, setWidth] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [text, setText] = useState("");

  useEffect(() => {
    if (!host.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(host.current);
    return () => observer.disconnect();
  }, []);

  // A new PDF: read it once; the pages are drawn by the effect below, and the old ones stay on screen until the new ones are ready.
  useEffect(() => {
    let live = true;
    loadPdfjs().then((pdfjs) => pdfjs.getDocument({ data: base64Bytes(pdf) }).promise).then(async (next) => {
      if (!live) return;
      const pages = [];
      for (let n = 1; n <= next.numPages; n += 1) {
        const page = await next.getPage(n);
        pages.push((await page.getTextContent()).items.map((item) => item.str).join(" "));
      }
      if (!live) return;
      doc.current = next;
      setPageCount(next.numPages);
      setText(pages.join("\n\f\n"));
      setLoaded((n) => n + 1);
    }).catch(() => live && onError?.("Aperçu indisponible."));
    return () => { live = false; };
  }, [pdf, onError]);

  useEffect(() => {
    if (!doc.current || !width || !holder.current) return undefined;
    const mine = ++runId.current;
    (async () => {
      const ratio = window.devicePixelRatio || 1;
      const canvases = [];
      for (let n = 1; n <= doc.current.numPages; n += 1) {
        const page = await doc.current.getPage(n);
        if (mine !== runId.current) return;
        const base = page.getViewport({ scale: 1 });
        const cssWidth = Math.min(width - 2 * PAGE_GAP_PX, 900);
        const viewport = page.getViewport({ scale: (cssWidth / base.width) * ratio });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${(cssWidth / base.width) * base.height}px`;
        canvas.className = "bg-white shadow-md";
        canvas.setAttribute("data-testid", `${testid}-page-${n}`);
        canvas.setAttribute("role", "img");
        canvas.setAttribute("aria-label", `Page ${n} sur ${doc.current.numPages}`);
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        if (mine !== runId.current) return;
        canvases.push(canvas);
      }
      holder.current?.replaceChildren(...canvases);
    })().catch(() => {});
    return () => { runId.current += 1; };
  }, [loaded, width, testid]);

  return (
    <div ref={host} className="w-full" data-testid={testid} data-pages={pageCount} data-text={text}>
      <div ref={holder} className="flex flex-col items-center py-3" style={{ gap: PAGE_GAP_PX, minHeight: 200 }} />
    </div>
  );
}

// The sheet the server draws is 210 mm wide (A4) on a 12 px desk; at 96 dpi that is 794 px + 24 px. The frame keeps that width and is
// scaled down to the panel, so the margins, the line lengths and the page breaks are those of the real page at any screen size.
const SHEET_PX = 794;
const FRAME_PX = SHEET_PX + 24;

// The page as the server renders it (POST /records/templates/{id}/preview): escaped text and the server's own stylesheet, shown in a
// sandboxed frame — never injected into the CRM's page. The sandbox grants NO script, form or navigation; `allow-same-origin` only lets
// this component read the sheet's height to size the frame (nothing can run inside it).
export function TemplatePreviewFrame({ preview, loading, error, testid = "template-preview", maxHeightClassName = "" }) {
  const host = useRef(null);
  const frame = useRef(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(1200);

  useEffect(() => {
    if (!host.current || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [preview]);

  const measure = useCallback(() => {
    const doc = frame.current?.contentDocument;
    if (doc?.documentElement) setHeight(Math.max(doc.documentElement.scrollHeight, 400));
  }, []);
  // A frame loaded while its tab was hidden (phone: « Modifier / Aperçu ») has no layout yet: measure again when the panel gets its width.
  useEffect(() => {
    if (!width) return undefined;
    measure();
    const t = setTimeout(measure, 200);
    return () => clearTimeout(t);
  }, [width, measure]);
  const loaded = useCallback(() => {
    measure();
    frame.current?.contentDocument?.fonts?.ready?.then(measure).catch(() => {});
    setTimeout(measure, 300);
  }, [measure]);

  if (error) {
    return <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>;
  }
  if (!preview) return <Skeleton className="h-[480px] w-full" />;
  if (preview.pdf) {
    return (
      <div className="space-y-2">
        {preview.missing?.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`${testid}-missing`}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>Sans valeur pour l'instant : {preview.missing.map((m) => m.label).join(", ")}.</span>
          </div>
        )}
        <div className="relative rounded-lg border border-slate-200 bg-slate-200">
          {loading && <Loader2 size={16} className="absolute right-3 top-3 z-10 animate-spin text-slate-500" aria-label="Mise à jour de l'aperçu" />}
          <div className={`overflow-x-hidden rounded-lg ${maxHeightClassName}`} data-testid={`${testid}-scroll`}>
            <PagedPreview pdf={preview.pdf} testid={testid} />
          </div>
        </div>
      </div>
    );
  }
  const scale = width > 0 ? Math.min(1, width / FRAME_PX) : 1;
  return (
    <div className="space-y-2">
      {preview.missing?.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`${testid}-missing`}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>Sans valeur pour l'instant : {preview.missing.map((m) => m.label).join(", ")}.</span>
        </div>
      )}
      <div className="relative rounded-lg border border-slate-200 bg-slate-200">
        {loading && <Loader2 size={16} className="absolute right-3 top-3 z-10 animate-spin text-slate-500" aria-label="Mise à jour de l'aperçu" />}
        <div ref={host} className={`overflow-x-hidden rounded-lg ${maxHeightClassName}`} data-testid={`${testid}-scroll`}>
          <div style={{ height: height * scale, width: FRAME_PX * scale, margin: "0 auto" }}>
            <iframe ref={frame} title="Aperçu du document" sandbox="allow-same-origin" srcDoc={preview.html} onLoad={loaded}
              style={{ width: FRAME_PX, height, border: 0, transform: `scale(${scale})`, transformOrigin: "top left", display: "block" }}
              data-testid={testid} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function TemplateStatus({ template }) {
  const parts = [];
  if (template.published_number) parts.push(<span key="p" className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">Version {template.published_number} publiée</span>);
  if (template.has_draft) parts.push(<span key="d" className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Brouillon en cours</span>);
  if (!template.active) parts.push(<span key="i" className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500">Désactivé</span>);
  return <span className="inline-flex flex-wrap gap-1.5">{parts}</span>;
}

// Scrolls the drawn preview to the page where a heading of the plan starts (the page text is the one PagedPreview reads from the PDF).
// A preview that was hidden (phone: « Version / Aperçu ») draws its pages only once shown, so the page's sheet is waited for a moment.
export async function goToPreviewPage(testid, outline, item) {
  for (let attempt = 0; attempt < 25; attempt += 1) {
    const root = document.querySelector(`[data-testid="${testid}"]`);
    const page = pageOfHeading((root?.dataset.text || "").split("\n\f\n"), outline, item);
    if (!page && root?.dataset.text) return false;
    const sheet = page && root.querySelector(`[data-testid="${testid}-page-${page}"]`);
    if (sheet) {
      sheet.scrollIntoView({ block: "start", behavior: "smooth" });
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return false;
}

/** The sections and articles of the published version, each one a link to its page in the preview. */
export function TemplateOutline({ blocks, previewTestid = "template-preview", title = "Plan du document", onBeforeGo }) {
  const outline = outlineOf(blocks);
  if (!outline.length) return null;
  const go = (item) => {
    onBeforeGo?.();
    goToPreviewPage(previewTestid, outline, item).then((found) => { if (!found) toast.info("Cette partie n'a pas été retrouvée dans l'aperçu."); });
  };
  return (
    <Card className="p-4 space-y-2" data-testid="template-outline">
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <ol className="space-y-0.5 text-sm">
        {outline.map((item, i) => (
          <li key={i} className={item.level === 2 ? "ml-4" : ""}>
            {item.navigable ? (
              <button type="button" onClick={() => go(item)} className="flex w-full items-baseline gap-2 rounded px-1 py-1 text-left text-slate-700 hover:bg-slate-100 hover:text-jamora" data-testid="template-outline-item">
                {item.number != null && <span className="w-14 shrink-0 text-xs text-slate-400">Article {item.number}</span>}
                <span className={item.kind === "heading" && item.level === 1 ? "font-medium" : ""}>{item.text}</span>
              </button>
            ) : (
              <span className="flex items-baseline gap-2 px-1 py-1 text-slate-500">
                {item.number != null && <span className="w-14 shrink-0 text-xs text-slate-400">Article {item.number}</span>}<span>{item.text}</span>
              </span>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
